/**
 * PATH: apps/hr/src/pages/inbox/inboxHelpers.ts
 * FILE_ID: HR-INBOX-HELPERS-001
 * PURPOSE: Pure I→O helpers for the supervisor approval queue.
 *          Confidence-band classification, date formatters, error
 *          extraction. No React, no DOM — unit-testable in isolation.
 *
 * WHY: Phase 3 of HR_UNIFICATION_PLAN.md. The page renders one
 *      row per submitted timesheet; the badge colour, sort key,
 *      and friendly date all come from these helpers so the
 *      InboxPage component stays presentation-focused.
 */
import type { ConfidenceBand, InboxItem } from './inboxTypes'

/**
 * Categorise a numeric confidence into a UI band. Thresholds match
 * the BE service classification:
 *   - >= 0.90 → green (in_geofence + clean AI)
 *   - >= 0.65 → amber (out_geofence, mild anomalies)
 *   - <  0.65 → red   (no_gps, multiple anomalies)
 */
export function confidenceBand(confidence: number): ConfidenceBand {
  if (confidence >= 0.9) return 'green'
  if (confidence >= 0.65) return 'amber'
  return 'red'
}

/**
 * Friendly summary like "8.5h, 2 entries". Empty strings stripped so
 * the row stays compact when an entry count is zero (it shouldn't
 * happen, but defending against odd BE shapes saves a ticket).
 */
export function formatRowSummary(item: InboxItem): string {
  const parts: string[] = []
  parts.push(`${item.total_hours.toFixed(1)}h`)
  if (item.entry_count > 0) {
    parts.push(`${item.entry_count} ${item.entry_count === 1 ? 'entry' : 'entries'}`)
  }
  return parts.join(' · ')
}

/**
 * Format an ISO submitted_at into "May 5, 2026 · 7:23 PM".
 * Falls back to "—" for null/undefined inputs so the cell never
 * renders as "Invalid Date".
 */
export function formatSubmittedAt(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * Pull a friendly error code/message out of any error thrown by the
 * workforceApi fetch wrapper. Mirrors the helper in the clock page.
 */
export function extractErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message
  if (typeof err === 'string') return err
  try {
    return JSON.stringify(err)
  } catch {
    return 'Unknown error'
  }
}

/**
 * Tooltip text for the confidence pill — surfaces the geofence reason
 * if the BE included one, otherwise a generic band label.
 */
export function describeConfidence(item: InboxItem): string {
  const band = confidenceBand(item.confidence)
  if (item.anomaly_reasons.length > 0) {
    return item.anomaly_reasons.join(' · ')
  }
  if (band === 'red') return 'Low confidence — needs review'
  if (band === 'amber') return 'Amber — verify before approving'
  return 'High confidence — clean punch'
}
